import type { ContentPage } from "../types";

// Every feature here is in the app, so re-check against the code when it
// changes: categories and sort orders from components/voices/VoiceSortTabs.tsx
// and the studio voice picker tabs (messages/studio.ts "studio.voices.*");
// text-to-speech limits and options from components/modals/
// TextToSpeechInputView.js (2,000 characters, 2 base voices, 9 accents,
// 7 deliveries) and services/conversion.ts (TTS audio is converted into the
// chosen voice); 6 voices per cover from conversionRequest.js MAX_VOICES;
// pitch, autotune and mix settings from messages/studioOptions.ts.
// Never name a character, show, game or community voice on this page.
const page: ContentPage = {
  section: "blog",
  slug: "ai-character-voices",
  coverLabel: "Character voices",
  title: "AI Character Voices: Make Covers and Text-to-Speech",
  metaDescription:
    "Make covers and text-to-speech in AI character voices: find anime, cartoon and gaming voices on Jammable, pick the right one and get clean results.",
  h1: "AI character voices: covers and text-to-speech",
  kind: "Guide",
  stats: [
    {
      value: "60,000+",
      label: "community voices, with anime, cartoon and gaming categories",
    },
    { value: "2,000", label: "characters of text per text-to-speech clip" },
    { value: "6", label: "voices you can put in one cover" },
  ],
  answer:
    "To make an AI character voice on Jammable, open the voice library, filter by anime, cartoon or gaming, and pick a voice. Then either add audio to make a cover, or type up to 2,000 characters for text-to-speech in that voice. It runs in the browser; making covers and text-to-speech needs a plan, from $1.99 for the first month.",
  takeaways: [
    "Character voices sit in three categories in the library: anime, cartoon and gaming.",
    "A cover re-sings a vocal in the character's voice; text-to-speech reads your words in it.",
    "Text-to-speech takes up to 2,000 characters, with a choice of accent and delivery.",
    "Pick voices with clear demos and lots of uses, and set the pitch to suit the voice's range.",
    "Every voice is made by the community, and you can train your own.",
  ],
  queries: [
    "character voice generator",
    "ai voice characters",
    "character ai voice generator",
    "character text to speech",
    "anime ai voice",
    "ai character voice",
    "cartoon voice generator",
  ],
  published: "2026-10-17",
  updated: "2026-10-17",
  blocks: [
    {
      type: "md",
      text: `## What is an AI character voice generator?

An AI character voice generator takes audio or text and gives it back in the voice of a character. On Jammable that voice is a trained AI voice model: someone in the community trained it on recordings of one voice, and Jammable uses it to re-sing a vocal (a cover) or to read out words you type (text-to-speech).

Jammable has more than 60,000 community voices. Character voices are a big part of the library, and they are grouped into three categories, so you don't have to dig through music voices to find them.`,
    },
    {
      type: "table",
      heading: "Where to find character voices",
      table: {
        columns: ["Category", "What's in it", "Open it"],
        rows: [
          [
            "Anime",
            "Voices from anime series and anime-style characters",
            "jammable.com/models?filter=anime",
          ],
          [
            "Cartoon",
            "Voices from animated shows and films",
            "jammable.com/models?filter=cartoon",
          ],
          [
            "Gaming",
            "Voices from video game characters",
            "jammable.com/models?filter=gaming",
          ],
        ],
      },
    },
    {
      type: "md",
      text: `Open the [anime voices](/models?filter=anime), [cartoon voices](/models?filter=cartoon) or [gaming voices](/models?filter=gaming) directly, or start from the full [voice library](/models) and tap a category.

## How to find the right character voice

There are three ways in, and they work together:

- **Search.** Type the name of the character into the search box in the [voice library](/models) or in the voice picker on the [cover maker](/create). The same character can have several versions made by different people, so it is worth comparing a few.
- **Sort.** The library sorts by **Trending today**, **Most used**, **Most liked** and **Newest**. Trending shows what people are using right now; Most used and Most liked are a good shortcut to voices that already sound right.
- **Browse a category.** The cover maker's voice picker has Anime, Cartoon and Gaming tabs next to Trending, Recently used and My voices, so you can switch voices without leaving the page.

Each voice has its own page, and most have demos to listen to. Listen before you pick: two versions of the same character can sound quite different.`,
    },
    {
      type: "steps",
      heading: "How to make a cover in a character voice",
      items: [
        {
          title: "Open the cover maker",
          body: "Go to [Create](/create), or press the button on a voice's page to start a cover with that voice already picked.",
        },
        {
          title: "Pick the character voice",
          body: "Press **Pick a voice**, then search or open the Anime, Cartoon or Gaming tab. Add more voices with **Add a voice** to make a duet of up to 6 voices.",
        },
        {
          title: "Add your audio",
          body: "Upload a file, record straight from your microphone, or choose **AI song** to describe a style and have a new song written for the voice to sing.",
        },
        {
          title: "Set the sound",
          body: "Change the pitch in semitones if the character sings higher or lower than the original vocal, and add autotune or an effect if you want one. If your audio is only vocals, turn on **My audio is just vocals**.",
        },
        {
          title: "Create the cover",
          body: "Press **Create cover**. Jammable separates the vocal, re-sings it in the character's voice and mixes it back with the music.",
        },
      ],
    },
    {
      type: "steps",
      heading: "How to use character text-to-speech",
      items: [
        {
          title: "Pick the character voice",
          body: "On [Create](/create), press **Pick a voice** and choose the character, the same way as for a cover.",
        },
        {
          title: "Choose Type words",
          body: "Under the audio options, choose **Type words** and write what the character should say, up to 2,000 characters.",
        },
        {
          title: "Pick a base voice, accent and delivery",
          body: "Choose a female or male base voice, one of 9 accents (American, British, French, German, Spanish, Italian, Korean, Australian or Japanese) and one of 7 deliveries: default, whispered, happy, sad, angry, shouting or terrified.",
        },
        {
          title: "Create it",
          body: "Press **Use these words**, then create. Jammable reads your text in the base voice and converts it into the character's voice.",
        },
      ],
    },
    {
      type: "matrix",
      heading: "Cover or text-to-speech?",
      matrix: {
        tools: ["Cover", "Text-to-speech"],
        rows: [
          {
            feature: "You give it",
            values: [
              "A vocal: an upload, a recording or an AI song",
              "Up to 2,000 characters of text",
            ],
          },
          {
            feature: "You get",
            values: [
              "The vocal re-sung in the character's voice, mixed with the music",
              "Your words spoken in the character's voice",
            ],
          },
          {
            feature: "Main settings",
            values: [
              "Pitch, autotune, effects, vocal volume",
              "Base voice, accent, delivery",
            ],
          },
          {
            feature: "Included in every plan",
            values: ["yes", "yes"],
          },
        ],
      },
    },
    {
      type: "verdicts",
      heading: "Which should I use?",
      items: [
        {
          useCase: "A character singing",
          pick: "Cover",
          why: "Add a vocal and Jammable re-sings it in the character's voice, keeping the melody and timing of the original.",
        },
        {
          useCase: "A character saying a line",
          pick: "Text-to-speech",
          why: "Type the line, pick a delivery like happy or shouting, and you get a spoken clip without recording anything.",
        },
        {
          useCase: "A character singing new lyrics",
          pick: "AI song",
          why: "Describe a style, write your own lyrics or let AI write them, and the character voice sings the result.",
        },
        {
          useCase: "Two characters in one track",
          pick: "Duet",
          why: "Add up to 6 voices to one cover and let Jammable switch between them, or choose who sings each part.",
        },
      ],
    },
    {
      type: "md",
      text: `## Tips for a character voice that sounds right

- **Choose a voice trained on clean audio.** A voice learns everything in its training audio, including music, crowd noise and echo. Listen to its demos: if it sounds muddy or has music bleeding through, try another version of the same character.
- **Check how much it is used.** Sort by Most used or Most liked. Voices that many people come back to usually sound good.
- **Match the voice's range.** A character voice works best near the pitch it was trained on. If a high-pitched cartoon voice sounds strained on a low vocal, raise the pitch; the cover maker suggests +12 semitones going from a male to a female voice, and -12 the other way.
- **Give it a clean vocal.** A single, clear lead vocal gives the cleanest cover. If the song has harmonies or ad-libs, turn on **Split out backing vocals**.
- **Go easy on effects.** Character voices often have a strong tone of their own. Heavy autotune or reverb on top can hide it.
- **For text-to-speech, write how the character talks.** Short sentences and punctuation give clearer phrasing, and the delivery setting does more for emotion than extra words.

## How character voices get made

Every voice on Jammable was trained by someone in the community. To make one, a person uploads or records at least 3 minutes of a single, clean voice (6 to 10 minutes sounds better) on the [Train](/train) page, and a 10-minute dataset is usually ready in about 25 minutes. They can keep the voice private or share it in the public library, which is how the anime, cartoon and gaming categories keep growing.

You can do the same: train your own voice, or a voice whose owner has agreed, and use it for covers and text-to-speech like any other. Our guide on [how to train your own AI voice model](/blog/how-to-train-an-ai-voice-model) walks through it. Training is included on the Creator plan, and you can also upload RVC models you trained elsewhere.

## How to share your character covers

Every cover gets its own page with a link you can send to anyone, and the link shows a preview card when you post it. You can also download the audio. On Creator you can turn a cover into a Marble Run video: the voices race, the one in the lead sings, and the lyrics are on screen, ready to post on TikTok and Reels.

## Does it cost anything?

You can browse every character voice, listen to demos and set up a cover for free. Making covers and text-to-speech needs a plan: Basic is $1.99 for the first month, then $8.99 a month, with unlimited covers. See [pricing](/pricing) for every plan.`,
    },
    {
      type: "cta",
      href: "/models?filter=anime",
      label: "Browse anime voices",
      text: "Find a character voice and make your first cover or text-to-speech clip.",
    },
  ],
  faqs: [
    {
      q: "How do I make an AI character voice?",
      a: "On Jammable, open the voice library, choose the anime, cartoon or gaming category, and pick a voice. Then add audio to make a cover, or choose Type words to make text-to-speech in that voice. You can also train a new voice from at least 3 minutes of clean audio.",
    },
    {
      q: "Is there a character text-to-speech generator?",
      a: "Yes. On Jammable, pick any character voice on the cover maker, choose Type words and enter up to 2,000 characters. You choose a female or male base voice, one of 9 accents and one of 7 deliveries, and Jammable converts the speech into the character's voice.",
    },
    {
      q: "Where can I find anime AI voices?",
      a: "Jammable's voice library has an anime category at jammable.com/models?filter=anime, next to cartoon and gaming. You can sort it by trending, most used, most liked or newest, or search for a character by name.",
    },
    {
      q: "Are AI character voices free?",
      a: "You can browse and listen to every character voice on Jammable for free and set up a cover. Making covers and text-to-speech needs a plan, from $1.99 for the first month, then $8.99 a month with unlimited covers.",
    },
    {
      q: "Can I put two character voices in one song?",
      a: "Yes. Add up to 6 voices to one cover. Jammable switches voices wherever the singing changes, or you can choose who sings each part yourself.",
    },
    {
      q: "Can I use AI character voices live on calls or streams?",
      a: "No. Jammable makes covers and text-to-speech clips from audio and text you give it; it does not change your voice in real time.",
    },
  ],
  related: [
    { label: "Anime voices", href: "/models?filter=anime" },
    { label: "Cartoon voices", href: "/models?filter=cartoon" },
    { label: "Gaming voices", href: "/models?filter=gaming" },
    { label: "Make a cover", href: "/create" },
    {
      label: "Train your own voice",
      href: "/blog/how-to-train-an-ai-voice-model",
    },
  ],
};

export default page;
